import { Reveal, SectionLabel } from "./Reveal";
import { ArrowRight } from "lucide-react";

const STEPS = [
  { n: "01", name: "Connect", body: "A source comes online. Raw events land as immutable facts — an actor, an action, a timestamp. Nothing summarized, because a summary is where provenance goes to die." },
  { n: "02", name: "Resolve", body: "The system works out what those facts refer to. This repo builds that product. That product is what was sold to that account. Done once per entity, not once per query." },
  { n: "03", name: "Answer", body: "You ask. Retrieval runs inside your permission scope, reasons across sources, and returns an answer with citations and timestamps." },
  { n: "04", name: "Correct", body: "A human who knows says otherwise. Confirmations, dismissals, fixes — the highest-quality signal the system will ever receive." },
  { n: "05", name: "Retain", body: "The correction is written back permanently. The next person asking a related question inherits it. Nobody fixes the same thing twice." },
];

export const Loop = () => (
  <section id="loop" data-testid="loop-section" className="grid-lines-dark relative bg-black text-white">
    <div className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-36">
      <SectionLabel index="02" title="The compounding loop" dark />

      <Reveal className="mt-10">
        <h2 className="max-w-4xl text-[28px] font-bold leading-tight tracking-tight md:text-[40px]">
          Intelligence that does not accumulate{" "}
          <span className="text-white/40">is not intelligence.</span>
        </h2>
      </Reveal>
      <Reveal delay={0.1} className="mt-6 max-w-2xl">
        <p className="text-base leading-relaxed text-white/60 md:text-lg">
          Most software is exactly as capable on day 1000 as on day 1. Qwiri's floor rises every day
          it runs — and that is the only part a competitor cannot copy in a quarter.
        </p>
      </Reveal>

      <div className="mt-16 grid gap-px border border-white/20 bg-white/20 md:grid-cols-5">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.08} className="h-full">
            <div
              data-testid={`loop-step-${s.name.toLowerCase()}`}
              className="group flex h-full flex-col bg-black p-6 transition-colors duration-150 hover:bg-white hover:text-black md:p-7"
            >
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40 group-hover:text-black/50">
                  Step {s.n}
                </span>
                {i < STEPS.length - 1 ? (
                  <ArrowRight size={16} className="text-white/40 group-hover:text-black" />
                ) : (
                  <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/40 group-hover:text-black/50">
                    ↺ again
                  </span>
                )}
              </div>
              <h3 className="mt-6 text-xl font-extrabold tracking-tight md:text-2xl">{s.name}</h3>
              <p className="mt-3 text-sm leading-relaxed text-white/55 group-hover:text-black/65">{s.body}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.2} className="mt-12">
        <p className="text-center text-[11px] font-bold uppercase tracking-[0.25em] text-white/50 md:text-[13px]" data-testid="loop-tagline">
          Connect → Resolve → Answer → Correct → Retain → Connect
        </p>
      </Reveal>
    </div>
  </section>
);
